import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { ChangePasswordDto } from "../../../model/user/dto/ChangePasswordDto";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { userActions } from "../../../redux/user/user.slice";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { SettingsWrapper } from "../components/SettingsWrapper";
import { Info } from "../../../components/Info";

const SaveButton = styled(Button)`
  margin: 0;
  width: fit-content;
  margin-top: ${Spacing.m};
`;

export const SettingsSecurityAndPassword: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const {
    control,
    getValues,
    handleSubmit,
    watch,
    setError,
    trigger,
    reset,
    formState: { dirtyFields, isValid },
  } = useForm<ChangePasswordDto & { newPasswordConfirm?: string }>({
    defaultValues: {
      newPassword: "",
      oldPassword: "",
      newPasswordConfirm: "",
      userId,
    },
  });

  const [newPassword, newPasswordConfirm] = watch([
    "newPassword",
    "newPasswordConfirm",
  ]);

  const onSubmit = async () => {
    try {
      setLoading(true);

      const dto = getValues();

      delete dto.newPasswordConfirm;

      await dispatch(userActions.changePassword(dto)).unwrap();
      AtiraToast.success(t("settings.update.success"));

      reset();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dirtyFields.newPassword && dirtyFields.newPasswordConfirm) {
      trigger(["newPassword", "newPasswordConfirm"]);
    }
  }, [newPassword, newPasswordConfirm, trigger, dirtyFields]);

  return (
    <SettingsWrapper>
      <Text fontWeight={"bold"}>
        {t("settings.securityPassword.changePassword.title")}
      </Text>

      <Info info={t("settings.securityPassword.changePassword.description")} />

      <Controller
        control={control}
        name="oldPassword"
        rules={{
          required: t("common.this_field_required"),
          minLength: {
            value: 6,
            message: t("settings.securityPassword.changePassword.error.range"),
          },
          maxLength: {
            value: 64,
            message: t("settings.securityPassword.changePassword.error.range"),
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Input
            title={t(
              "settings.securityPassword.changePassword.oldPassword.title",
            )}
            value={value}
            onChange={onChange}
            valid={!error}
            errorMessage={error?.message}
            type="password"
            required
            autoComplete="current-password"
          />
        )}
      />

      <Controller
        control={control}
        name="newPassword"
        rules={{
          required: t("common.this_field_required"),
          minLength: {
            value: 6,
            message: t("settings.securityPassword.changePassword.error.range"),
          },
          maxLength: {
            value: 64,
            message: t("settings.securityPassword.changePassword.error.range"),
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Input
            title={t(
              "settings.securityPassword.changePassword.newPassword.title",
            )}
            value={value}
            onChange={onChange}
            valid={!error}
            errorMessage={error?.message}
            type="password"
            required
            autoComplete="new-password"
          />
        )}
      />

      <Controller
        control={control}
        name="newPasswordConfirm"
        rules={{
          required: t("common.this_field_required"),
          minLength: {
            value: 6,
            message: t("settings.securityPassword.changePassword.error.range"),
          },
          maxLength: {
            value: 64,
            message: t("settings.securityPassword.changePassword.error.range"),
          },
          validate: (value) =>
            value === getValues("newPassword") ||
            t("settings.securityPassword.changePassword.error.match"),
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Input
            title={t(
              "settings.securityPassword.changePassword.confirmPassword.title",
            )}
            value={value}
            onChange={onChange}
            valid={!error}
            errorMessage={error?.message}
            type="password"
            required
            autoComplete="new-password"
          />
        )}
      />

      <SaveButton
        title={t("common.save")}
        onClick={handleSubmit(onSubmit)}
        loading={loading}
        disabled={!isValid}
      />
    </SettingsWrapper>
  );
};
